import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Warehouse as WarehouseIcon, X } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function NewWarehouseModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [isPartOfBusinessLocation, setIsPartOfBusinessLocation] = useState(true);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [purchasing, setPurchasing] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.createWarehouse({
        name,
        isPartOfBusinessLocation,
        address: isPartOfBusinessLocation ? null : address,
        city: isPartOfBusinessLocation ? null : city,
        postalCode: isPartOfBusinessLocation ? null : postalCode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      queryClient.invalidateQueries({ queryKey: ["entitlements"] });
      onClose();
    },
  });

  const isLimitReached =
    save.isError && save.error instanceof ApiError && save.error.response.errorCode === "WAREHOUSE_LIMIT_REACHED";

  // "Svaki dodatni magacin je plaćen dodatak" - vidi razgovor. Umesto samo
  // greške, nudi odmah kupovinu jednog dodatnog magacina (ista ponuda-pa-
  // plati logika kao "Aktiviraj dodatak", vidi ModuleGateNotice) preko
  // dugmeta "+" - kao za Finora Mobile aplikaciju.
  const handleBuyExtraWarehouse = async () => {
    setPurchasing(true);
    try {
      await api.purchaseExtraWarehouse();
      navigate("/moji-racuni");
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novi magacin</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="block text-sm font-medium text-slate-700">Naziv magacina *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isPartOfBusinessLocation}
              onChange={(e) => setIsPartOfBusinessLocation(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Magacin je deo prodajnog objekta (koristi njegovu adresu)
          </label>
          {!isPartOfBusinessLocation && (
            <div className="space-y-3 rounded-lg border border-border bg-app-bg p-3">
              <div>
                <label className="block text-xs font-medium text-slate-500">Adresa</label>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="mt-1 h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500">Mesto</label>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1 h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500">Poštanski broj</label>
                  <input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="mt-1 h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}
          {isLimitReached && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {save.error instanceof ApiError ? save.error.response.message : "Dostignut je limit magacina."}
            </div>
          )}
          {save.isError && !isLimitReached && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          {isLimitReached ? (
            <Button className="flex-1" disabled={purchasing} onClick={handleBuyExtraWarehouse}>
              {purchasing ? "Kreiranje ponude…" : "Aktiviraj dodatni magacin"}
            </Button>
          ) : (
            <Button className="flex-1" disabled={!name || save.isPending} onClick={() => save.mutate()}>
              Sačuvaj
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function WarehousesTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const { data: warehouses, isLoading } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => api.listWarehouses(),
  });
  // "1 magacin besplatan, svaki naredni je plaćen dodatak preko + kao za
  // Finora Mobile aplikaciju" - vidi razgovor.
  const { data: entitlements } = useQuery({
    queryKey: ["entitlements"],
    queryFn: () => api.getEntitlements(),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {entitlements ? (
          <p className="text-sm text-slate-500">
            Magacini: <span className="font-medium text-slate-700">{entitlements.warehouses.used}</span> /{" "}
            {entitlements.warehouses.included} uključeno
            {Number(entitlements.warehouses.additionalPrice) > 0 && (
              <span className="text-slate-400">
                {" "}
                (dodatni {entitlements.warehouses.additionalPriceGross} RSD/mesečno sa PDV-om)
              </span>
            )}
          </p>
        ) : (
          <span />
        )}
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi magacin
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !warehouses || warehouses.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={WarehouseIcon} title="Nema magacina" description="Podrazumevani magacin će se kreirati automatski." />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>Tip</th>
                <th>Adresa</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((w) => (
                <tr key={w.id}>
                  <td className="font-medium text-slate-800">
                    {w.name} {w.isDefault && <span className="ml-1 text-xs text-slate-400">(podrazumevani)</span>}
                  </td>
                  <td className="text-slate-500">
                    {w.isPartOfBusinessLocation ? "Deo prodajnog objekta" : "Samostalan"}
                  </td>
                  <td className="text-slate-500">
                    {w.isPartOfBusinessLocation ? "—" : [w.address, w.city, w.postalCode].filter(Boolean).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && <NewWarehouseModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}
